import './ReportFormDocument.scss';
import { HvacResults, Uuid } from '@belimo-retrofit-portal/logic';
import { ReadonlyRecord } from 'fp-ts/ReadonlyRecord';
import React, { useMemo, useRef } from 'react';
import { PdfMetadata } from 'src/modules/common/types/PdfMetadata';
import { Project } from 'src/modules/common/types/Project';
import { UserSettings } from 'src/modules/common/types/UserSettings';
import { OutputDocumentContext, OutputDocumentContextData } from 'src/modules/output/context/OutputDocumentContext';
import { OutputPageContext } from 'src/modules/output/context/OutputPageContext';
import { OutputDocumentPageInfo } from 'src/modules/output/types/OutputDocumentPageInfo';
import { ReportContentPage } from 'src/modules/report/types/ReportContent';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGenerated } from 'src/modules/report/types/ReportGenerated';
import { ReportDocumentContent } from 'src/modules/report/views/ReportDocumentContent';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly language: Language;
  readonly document: ReportGenerated;
  readonly formData: ReportFormData;
  readonly project: Project;
  readonly onChange: (pages: ReadonlyArray<ReportContentPage>) => void;
  readonly userSettings: UserSettings;
  readonly embeddings: ReadonlyRecord<Uuid, PdfMetadata>;
  readonly results: HvacResults;
};

export const ReportFormDocument = React.memo(({
  language, document, formData, project, onChange, userSettings, embeddings, results,
}: Props): React.ReactElement => {
  const pageContents = useRef(new Map<string, OutputDocumentPageInfo>());
  const documentContext = useMemo((): OutputDocumentContextData => ({
    registerPage: (page: OutputDocumentPageInfo): void => {
      pageContents.current = pageContents.current.set(page.id, page);

      const content = getReportPages(
        pageContents.current,
        formData.appendix.enabled ? embeddings : {},
      );
      onChange(content);
    },
    unregisterPage: (id: string): void => {
      pageContents.current.delete(id);

      const content = getReportPages(pageContents.current, formData.appendix.enabled ? embeddings : {});
      onChange(content);
    },
  }), [onChange, formData.appendix.enabled, embeddings]);

  return (
    <div className="bp-report-form-document">
      <OutputDocumentContext.Provider value={documentContext}>
        <ReportDocumentContent
          results={results}
          document={document}
          formData={formData}
          project={project}
          language={language}
          embeddings={embeddings}
          userSettings={userSettings}
        />
      </OutputDocumentContext.Provider>
    </div>
  );
});

function getReportPages(
  contents: ReadonlyMap<string, OutputDocumentPageInfo>,
  embeddings: ReadonlyRecord<Uuid, PdfMetadata>,
): ReportContentPage[] {
  const sortedPages = [...contents.values()].sort((a, b) => a.order - b.order);
  const embeddedPages = Object.values(embeddings).reduce(
    // amount of embedded pages adjusted on the first page
    (prev, embedding) => prev + (embedding.pageCount - 1),
    0,
  );
  const shownPages = sortedPages.filter((it) => it.visible);

  return sortedPages.map((page) => ({
    id: page.id,
    section: page.section,
    content: (
      <OutputPageContext.Provider
        value={{
          pageNumber: shownPages.indexOf(page) + 1,
          totalPages: shownPages.length + embeddedPages,
        }}
      >
        {page.content}
      </OutputPageContext.Provider>
    ),
  }));
}
